import type { CompanyGeolocationSite } from '.';

const ADDRESS_FIELDS = ['streetAddress', 'addressLocality', 'addressRegion', 'postalCode', 'addressCountry'] as const;

type AddressField = (typeof ADDRESS_FIELDS)[number];

/** The fields of an edit to a saved site that decide which of its saved values still hold. */
export type SiteEditFields = Partial<Record<AddressField, string | null>> & { supplyChainPosition?: string };

/**
 * Which saved values of a site an edit makes untrue, when the edit does not give new ones itself.
 *
 *  - `coordinates`: coordinates belong to the address. An edit that changes any part of it
 *    (ignoring case and spacing) leaves the saved pair pointing at where the site used to be:
 *    kept, a site that moved from Espoo to Tampere would be scored at Espoo with nothing to say
 *    so, whereas a cleared pair shows the site as not climate-scored until it is geocoded.
 *  - `weight`: a weight is a share of its own value-chain position, so a site moved to another
 *    position does not take its old share along.
 *
 * An absent field is not a change. Shared by the chat card's proposal merge and the
 * `save_site_locations` chat tool, so an edit leaves a site the same way through either.
 */
export function savedValuesInvalidatedByEdit(
  edit: SiteEditFields,
  saved: Pick<CompanyGeolocationSite, AddressField | 'supplyChainPosition'>,
): { coordinates: boolean; weight: boolean } {
  const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();
  return {
    coordinates: ADDRESS_FIELDS.some(field => edit[field] !== undefined && norm(edit[field]) !== norm(saved[field])),
    weight: edit.supplyChainPosition !== undefined && edit.supplyChainPosition !== saved.supplyChainPosition,
  };
}
